import styled from 'styled-components';
import {comum} from '../../../../styles/Themes/light';

export const Container = styled.div`
  padding-bottom: 40px;
  background-color: ${(props: any) => props.theme.colors.interfacePure0};
  border-radius: 8px;
`;

export const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${(props: any) => props.theme.colors.c2d3748};
  margin-bottom: 24px;
  border-bottom: 1px solid ${(props: any) => props.theme.colors.ce2e8f0};
  padding-bottom: 8px;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Container especial para a linha com 3 campos (Valor do Cliente, Valor do Serviço, Prazo Fatal)
export const ThreeColumnRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr; // Três colunas iguais
  gap: 24px;
  grid-column: 1 / -1; // Ocupa toda a largura do grid pai
  width: 100%;
  @media (max-width: 950px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Label = styled.label<{ required?: boolean }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(props: any) => props.theme.colors.c2d3748};
  margin-bottom: 0.5rem;
  display: block;
  position: relative;

  ${props => props.required && `
    &::after {
      content: '*';
      color: ${(props: any) => props.theme.colors.ce53e3e};
      margin-left: 4px;
      font-weight: 700;
    }
  `}
`;

export const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  height: 48px;
  padding: 0 1rem;
  border: ${props => props.hasError ? '1px solid #E53E3E' : '1px solid var(--ce2e8f0)'};
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  transition: border-color 0.2s;

  &:hover {
    border: 1px solid ${(props: any) => props.theme.colors.c000000};
  }

  &:focus {
    border: 1px solid ${(props: any) => props.theme.colors.c000000};
  }

  &:blur {
    border: ${props => props.hasError ? '1px solid #E53E3E' : '1px solid var(--ce2e8f0)'};
  }
`;

export const ErrorMessage = styled.span`
  color: ${(props: any) => props.theme.colors.ce53e3e};
  font-size: 0.875rem;
  margin-top: 4px;
  display: block;
`;

export const ErrorSpacing = styled.span`
  color: ${(props: any) => props.theme.colors.pureWhite};
  font-size: 0.875rem;
  margin-top: 4px;
  display: block;
`;

// Estilos para os selects (Estado e Comarca)
export const selectStyles = {
  control: (provided: Record<string, unknown>, state: any) => ({
    ...provided,
    height: 48,
    boxShadow: "none",
    cursor: 'pointer',
    border: state.hasValue && state.selectProps.error 
      ? `1px solid ${comum.colors.primaryRed400}` 
      : '1px solid var(--ce2e8f0)',
    '&:hover': {
      border: '1px solid var(--c000000)',
    },
    '&:focus': {
      border: '1px solid var(--c000000)',
    },
  }),
  placeholder: (provided: Record<string, unknown>) => ({
    ...provided,
    color: 'var(--c353a40)',
    fontSize: '16px',
    fontWeight: '400',
  }),
  singleValue: (provided: Record<string, unknown>) => ({
    ...provided,
    color: 'var(--c353a40)',
    fontSize: '16px',
  }),
  option: (provided: Record<string, unknown>) => ({
    ...provided,
    color: 'var(--c5a646e)',
    fontSize: '16px',
    fontWeight: '500',
  }),
  menu: (provided: Record<string, unknown>) => ({
    ...provided,
    zIndex: 3,
  }),
  menuPortal: (provided: Record<string, unknown>) => ({
    ...provided,
    zIndex: 3,
  }),
};

export const selectTheme = (theme: any) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: comum.colors.interfacePure100,
    primary50: comum.colors.interfacePure100,
    primary: comum.colors.interfacePure100,
  },
});

export const selectComponents = {
  IndicatorSeparator: () => null
};

// Estilos customizados para o campo de fuso horário (menor)
export const createTimezoneSelectStyles = (hasError: boolean) => ({
  ...selectStyles,
  control: (provided: Record<string, unknown>, state: any) => ({
    ...selectStyles.control(provided, state),
    boxSizing: 'border-box' as const,
    width: '100px', // Largura fixa para o seletor de fuso
    minWidth: '100px',
    maxWidth: '100px',
    height: '48px', // Altura fixa para alinhar com outros campos
    borderRadius: '0px 8px 8px 0px', // Cantos arredondados apenas na direita
    border: hasError 
      ? `1px solid ${comum.colors.primaryRed400}` 
      : state.isFocused || state.menuIsOpen 
        ? '1px solid var(--c000000)' 
        : '1px solid var(--ce2e8f0)', // Contorno preto quando focado ou menu aberto
    '&:hover': {
      border: hasError 
        ? `1px solid ${comum.colors.primaryRed400}` 
        : '1px solid var(--c000000)',
      borderLeft: hasError 
        ? `1px solid ${comum.colors.primaryRed400}` 
        : '1px solid var(--c000000)',
    },
    '&:focus': {
      border: hasError 
        ? `1px solid ${comum.colors.primaryRed400}` 
        : '1px solid var(--c000000)',
      borderLeft: hasError 
        ? `1px solid ${comum.colors.primaryRed400}` 
        : '1px solid var(--c000000)',
    },
  }),
  singleValue: (provided: Record<string, unknown>) => ({
    ...provided,
    color: 'var(--c353a40)',
    fontSize: '16px',
    fontWeight: '600' // Deixa a sigla mais destacada
  }),
  menu: (provided: Record<string, unknown>) => ({
    ...provided,
    minWidth: '340px',
    width: 'auto',
    transform: `${(provided as any).transform || ''} translateX(calc(-100% + 100px))`
  }),
  menuList: (provided: Record<string, unknown>) => ({
    ...provided,
    minWidth: '340px'
  })
});

// Container para alinhar prazo fatal e fuso horário em paralelo
export const DateTimeContainer = styled.div`
  display: flex;
  gap: 0; // Removido o gap para "grudar" os campos
  align-items: flex-end;
  width: 100%; // Garante que ocupe toda a largura disponível (34%)
  height: 48px; // Altura fixa para alinhar com outros campos
  
  
  // Quando o dropdown do select está aberto, mantém o contorno
  .react-select__control--menu-is-open {
    border: 1px solid ${(props: any) => props.theme.colors.c000000} !important;
    border-left: 1px solid ${(props: any) => props.theme.colors.c000000} !important;
  }
  
  // Quando o select está focado (mesmo com dropdown fechado)
  .react-select__control--is-focused {
    border: 1px solid ${(props: any) => props.theme.colors.c000000} !important;
    border-left: 1px solid ${(props: any) => props.theme.colors.c000000} !important;
  }
`;

export const DateFieldContainer = styled(FieldContainer)`
  flex: 1;
  margin-right: 0; // Remove margem direita
  min-width: 0; // Permite que o campo se contraia se necessário
  
  input {
    height: 48px !important; // Altura fixa para alinhar com outros campos
    border-radius: 8px 0px 0px 8px !important; // Cantos arredondados apenas na esquerda
    border-right: none !important; // Remove borda direita para conectar com o fuso horário
  }
`;

export const TimezoneFieldContainer = styled(FieldContainer)`
  flex: 0 0 auto;
  margin-left: 0; // Remove margem esquerda
  min-width: 100px; // Largura mínima para o seletor de fuso
  max-width: 100px; // Largura máxima para o seletor de fuso
  
  .react-select__control {
    border-left: none !important; // Remove borda esquerda para conectar com o campo de data
    cursor: pointer !important; // Garante cursor de ponteiro
  }
  
  .react-select__single-value {
    cursor: pointer !important; // Garante cursor de ponteiro no texto
  }
  
  .react-select__input-container {
    cursor: pointer !important; // Garante cursor de ponteiro no container de input
  }
`;

// Estilos customizados para o DateTimePicker
export const DatePickerContainer = styled.div`
  flex: 1;
  width: 100%;
  
  input {
    box-sizing: border-box !important;
    width: 100% !important;
    height: 48px !important;
    border-radius: 8px 0px 0px 8px !important; // Cantos arredondados apenas na esquerda
    border-right: none !important; // Remove borda direita para conectar com o fuso horário
  }
  
  input {
    &:hover {
      border: 1px solid ${(props: any) => props.theme.colors.c000000} !important;
      border-right: 1px solid ${(props: any) => props.theme.colors.c000000} !important;
    }
    
    &:focus {
      border: 1px solid ${(props: any) => props.theme.colors.c000000} !important;
      border-right: 1px solid ${(props: any) => props.theme.colors.c000000} !important;
    }
  }
`;

export const VincularAContainer = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto minmax(20px, auto);
  column-gap: 24px;
  row-gap: 0;
  grid-column: 1 / -1;
  margin-bottom: 24px;
  align-items: start;
`;

export const VincularALabelAndTabWrapper = styled.div`
  grid-column: 1;
  grid-row: 1 / 3;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const VincularALabel = styled(Label)`
  text-transform: uppercase;
  color: ${(props: any) => props.theme.colors.c6b7280};
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0;
  white-space: nowrap;
  letter-spacing: 0.05em;
`;

export const VincularAFormControl = styled.div`
  grid-column: 2;
  grid-row: 1 / -1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto minmax(20px, auto);
  gap: 8px 16px;
  
  ${Label} {
    margin-bottom: 0;
    font-size: 0.875rem;
    font-weight: 600;
    color: ${(props: any) => props.theme.colors.c2d3748};
  }
  
  ${Input} {
    height: 48px;
  }
  
  ${ErrorMessage} {
    margin-top: 0;
    min-height: 20px;
  }
`;

export const TipoParteContainer = styled.div`
  display: flex;
  gap: 0;
  background-color: ${(props: any) => props.theme.colors.cf3f4f6};
  border-radius: 8px;
  padding: 4px;
  width: fit-content;
`;

export const TipoParteButton = styled.button<{ selected: boolean }>`
  height: 40px;
  min-width: 120px;
  padding: 0 20px;
  border: none;
  background-color: ${props => props.selected ? 'var(--cffffff)' : 'transparent'};
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: ${props => props.selected ? '600' : '500'};
  color: ${props => props.selected ? 'var(--c1f2937)' : 'var(--c6b7280)'};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: ${props => props.selected ? '0 1px 2px 0 rgba(0, 0, 0, 0.05)' : 'none'};

  &:hover {
    color: ${props => props.selected ? 'var(--c1f2937)' : 'var(--c374151)'};
    background-color: ${props => props.selected ? 'var(--cffffff)' : 'rgba(255, 255, 255, 0.5)'};
  }

  &:focus {
    outline: none;
  }

  svg {
    width: 16px;
    height: 16px;
    color: ${props => props.selected ? comum.colors.primaryRed400 : 'currentColor'};
  }
`;

export const VincularAFieldsColumn = styled.div`
  display: contents;
  
  ${Label} {
    grid-row: 1;
    margin-bottom: 0;
    font-size: 0.875rem;
    font-weight: 600;
    color: ${(props: any) => props.theme.colors.c2d3748};
    
    &:nth-of-type(1) {
      grid-column: 1;
    }
    
    &:nth-of-type(2) {
      grid-column: 2;
    }
  }
  
  ${Input} {
    grid-row: 2;
    height: 48px;
    
    &:nth-of-type(1) {
      grid-column: 1;
    }
    
    &:nth-of-type(2) {
      grid-column: 2;
    }
  }
  
  ${ErrorMessage} {
    grid-row: 3;
    min-height: 20px;
    margin-top: 0;
    
    &:nth-of-type(1) {
      grid-column: 1;
    }
    
    &:nth-of-type(2) {
      grid-column: 2;
    }
  }
`;

export const ClienteFieldsContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  grid-column: 1 / -1;
  margin-bottom: 24px;
  
  ${FieldContainer} {
    flex: 1;
    min-width: 200px;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const SolicitanteSelectButton = styled.button<{ hasError?: boolean; hasValue?: boolean }>`
  width: 100%;
  height: 48px;
  padding: 0 1rem;
  border: ${props => props.hasError ? '1px solid #E53E3E' : '1px solid var(--ce2e8f0)'};
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: inherit;
  background-color: ${(props: any) => props.theme.colors.interfacePure0};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: border-color 0.2s;
  color: ${props => props.hasValue ? 'var(--c353a40)' : 'var(--c9ca3af)'};

  &:hover {
    border: 1px solid ${(props: any) => props.theme.colors.c000000};
  }

  &:focus {
    border: 1px solid ${(props: any) => props.theme.colors.c000000};
    outline: none;
  }

  .placeholder {
    color: ${(props: any) => props.theme.colors.c9ca3af};
  }

  .value {
    color: ${(props: any) => props.theme.colors.interfaceText500};
    font-weight: 500;
  }
`;

export const VincularASolicitanteWrapper = styled.div`
  display: contents;
  
  ${Label} {
    grid-row: 1;
    grid-column: 1 / -1;
    margin-bottom: 0;
    font-size: 0.875rem;
    font-weight: 600;
    color: ${(props: any) => props.theme.colors.c2d3748};
  }
  
  ${SolicitanteSelectButton} {
    grid-row: 2;
    grid-column: 1 / -1;
    height: 48px;
  }
  
  ${ErrorMessage} {
    grid-row: 3;
    grid-column: 1 / -1;
    margin-top: 0;
    min-height: 20px;
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const ModalContent = styled.div`
  background-color: ${(props: any) => props.theme.colors.interfacePure0};
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
`;

export const ModalHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${(props: any) => props.theme.colors.ce2e8f0};
  display: flex;
  justify-content: space-between;
  align-items: center;

  h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: ${(props: any) => props.theme.colors.c2d3748};
  }
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${(props: any) => props.theme.colors.c718096};
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background-color: ${(props: any) => props.theme.colors.cf7fafc};
    color: ${(props: any) => props.theme.colors.c2d3748};
  }
`;

export const SearchContainer = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid ${(props: any) => props.theme.colors.ce2e8f0};

  input {
    width: 100%;
    height: 40px;
    padding: 0 12px;
    border: 1px solid ${(props: any) => props.theme.colors.ce2e8f0};
    border-radius: 6px;
    font-size: 0.875rem;
    outline: none;

    &:focus {
      border-color: ${comum.colors.primaryRed400};
    }
  }
`;

export const OfficeList = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 8px;
`;

export const OfficeItem = styled.div<{ isSelected?: boolean }>`
  padding: 12px 16px;
  margin: 4px 0;
  border-radius: 6px;
  cursor: pointer;
  background-color: ${props => props.isSelected ? comum.colors.primaryRed50 : 'transparent'};
  border: 1px solid ${props => props.isSelected ? comum.colors.primaryRed400 : 'transparent'};

  &:hover {
    background-color: ${comum.colors.primaryRed50};
  }

  .office-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .office-name {
    font-size: 0.875rem;
    font-weight: 600;
    color: ${(props: any) => props.theme.colors.c2d3748};
  }

  .office-codes {
    font-size: 0.75rem;
    color: ${(props: any) => props.theme.colors.c718096};
    display: flex;
    gap: 8px;
    align-items: center;

    .code-label {
      font-weight: 500;
    }

    .code-new {
      font-weight: 600;
      color: ${comum.colors.primaryRed400};
    }

    .code-separator {
      color: ${(props: any) => props.theme.colors.ccbd5e0};
    }

    .code-old {
      color: ${(props: any) => props.theme.colors.c718096};
    }
  }
`;

export const NoResultsMessage = styled.div`
  padding: 32px;
  text-align: center;
  color: ${(props: any) => props.theme.colors.c718096};
  font-size: 0.875rem;
`;